import { styled } from '@material-ui/core';
import { Select, Space } from 'antd';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TextField from '../TextField';
import { countries, shortMonthYearFormat } from '../../helpers/AppHelpers';
import DatePicker from '../DatePicker';
import AutoComplete from '../AutoComplete';
import Checkbox from '../Checkbox';
import { InfoTooltip } from '../Tooltips/InfoTooltip';
import { convertQualificationTypesForSelect } from '../../constants/qualificationTypes';
import UploadIcon from '../../assets/images/upload-16.svg?react';
import PdfIcon from '../../assets/images/menu-icons/icon-pdf.svg?react';
import CloseIcon from '../../assets/images/icons/close-black.svg?react';

const QualificationForm = ({
  item,
  onChange,
  identifier = '',
  errors,
  setErrors,
  isDisabled = false,
  tagsList,
  qualificationTypes,
  tooltipType,
}) => {
  const { t } = useTranslation('leftSidebar');
  const fileInputRef = useRef(null);

  const qualificationTypeOptions = convertQualificationTypesForSelect(qualificationTypes);
  const selectedType = item.type ?? item.major;

  const handleUploadClick = () => {
    if (fileInputRef.current && !isDisabled) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, file: true }));
      return;
    }

    if (file.size > maxSize) {
      setErrors((prev) => ({ ...prev, file: true }));
      return;
    }

    onChange(`${identifier}file`, file);

    setErrors((prev) => ({ ...prev, file: false }));
  };

  const isValidUrl = (value) => {
    if (!value) return true;
    try {
      // eslint-disable-next-line
      new URL(value);
      return true;
    } catch {
      return false;
    }
  };

  const handleSkillsChange = (selectedTags) => {
    const newSkills = selectedTags
      .map((tagStr) => {
        const tagObj = tagsList.find((tg) => tg.tag === tagStr);
        if (!tagObj) return null;
        return {
          id: tagObj.id,
          personId: null,
          starRating: 0,
          tag: { ...tagObj },
          isTechnologyAdded: false,
          isIntegrationAdded: false,
          isTechnologyDeleted: false,
          isIntegrationDeleted: false,
          comment: null,
          updatedBy: null,
          updatedAt: null,
        };
      })
      .filter(Boolean);

    onChange(`${identifier}skills`, newSkills);
  };

  const handleStateAccreditedChange = (v) => {
    if (!v) {
      onChange(`${identifier}accreditationCountry`, '');
    }
    onChange(`${identifier}stateAccredited`, v);
  };

  const handleIssueDateChange = (v) => {
    onChange(`${identifier}issueDate`, v);
    if (item.expirationDate && v && item.expirationDate < v) {
      setTimeout(() => onChange(`${identifier}expirationDate`, null), 0);
    }
    setErrors((prev) => ({ ...prev, issueDate: false }));
  };

  return (
    <div>
      <Space size="small" direction="vertical" className="w-full mb-6">
        <label className="uppercase tracking-wide text-gray-600 text-xs font-semibold mb-2">
          <div className="flex flex-row gap-2 items-center">
            {t('qualifications.type.label')}
            <span>*</span>
            <InfoTooltip
              placement="right"
              data={tooltipType}
              label={t('qualifications.type.label')}
            />
          </div>
        </label>
        <CustomSelect
          className="w-full"
          placeholder={t('qualifications.type.placeholder')}
          value={selectedType}
          options={qualificationTypeOptions}
          onChange={(v) => {
            onChange(`${identifier}type`, v);
            setErrors((prev) => ({ ...prev, type: false }));
          }}
          disabled={isDisabled}
          status={errors.type ? 'error' : ''}
        />

        {errors.type && (
          <div className="text-red-600 text-xs mb-6">
            {t('qualifications.type.error') || 'Please select qualification type'}
          </div>
        )}
      </Space>
      <TextField
        className="mb-6"
        label={t('qualifications.name.label')}
        placeholder={t('qualifications.name.placeholder')}
        value={item.name ?? item.description ?? ''}
        star="true"
        validation={errors.name}
        onChange={(v) => {
          onChange(`${identifier}name`, v);
          setErrors((prev) => ({ ...prev, name: false }));
        }}
        disabled={isDisabled}
      />

      {errors.name && (
        <div className="text-red-600 text-xs mb-6">Please enter qualification name</div>
      )}

      <TextField
        className="mb-3"
        label={t('qualifications.link.label')}
        placeholder={t('qualifications.link.placeholder')}
        value={item.file ? '' : (item.credentialLink ?? item.link ?? '')}
        onChange={(v) => {
          const trimmed = item.file ? v : v.slice(0, 250);
          onChange(`${identifier}credentialLink`, trimmed);
          setErrors((prev) => ({
            ...prev,
            credentialLink: trimmed ? !isValidUrl(trimmed) : false,
          }));
        }}
        disabled={isDisabled || !!item.file}
      />

      {errors.credentialLink && (
        <div
          className="text-red-600 text-xs mt-1"
          style={{ display: item.file ? 'none' : 'block' }}
        >
          Please enter a valid URL (https://...)
        </div>
      )}

      {item.file && (item.file instanceof File ? item.file.name : item.file.fileName) ? (
        <div
          className="mb-6 flex items-center rounded"
          style={{
            padding: '6px',
            border: '1px solid #EDF2F7',
            cursor:
              (item.file.urlLink ?? item.file.credentialLink ?? item.file.link)
                ? 'pointer'
                : 'default',
          }}
          onClick={() => {
            const url = item.file.urlLink ?? item.file.credentialLink ?? item.file.link;
            if (url) {
              window.open(url, '_blank', 'noopener,noreferrer');
            }
          }}
        >
          {(item.file instanceof File ? item.file.type : item.file?.type) === 'application/pdf' && (
            <PdfIcon />
          )}
          <p style={{ fontWeight: 400, marginRight: 6 }}>
            {item.file instanceof File ? item.file.name : item.file?.fileName}
          </p>
          <CloseIcon
            style={{ width: 14, height: 14, justifySelf: 'end', cursor: 'pointer' }}
            onClick={(e) => {
              e.stopPropagation();
              onChange(`${identifier}file`, null);
            }}
          />
        </div>
      ) : (
        <div
          className="mb-6 flex cursor-pointer"
          style={{ color: '#3f51b5' }}
          onClick={handleUploadClick}
          disabled={isDisabled}
        >
          Or upload credential file
          <UploadIcon />
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
        disabled={isDisabled}
      />

      <TextField
        className="mb-6"
        label={t('qualifications.issued.label')}
        placeholder={t('qualifications.issued.placeholder')}
        value={item.issuedBy ?? item.issued ?? ''}
        star="true"
        validation={errors.issuedBy}
        onChange={(v) => {
          onChange(`${identifier}issuedBy`, v.slice(0, 250));
          setErrors((prev) => ({ ...prev, issuedBy: false }));
        }}
        disabled={isDisabled}
      />

      {errors.exists && (
        <div className="text-red-600 text-xs mb-6">
          A qualification with this type of qualification, name of qualification and issued by
          already exists
        </div>
      )}
      {errors.issuedBy && <div className="text-red-600 text-xs mb-6">Please enter issued by</div>}

      <div className="flex items-center gap-2 mb-6">
        <Checkbox
          checked={item.stateAccredited ?? item.isStateAccredited ?? false}
          onChange={(v) => handleStateAccreditedChange(v)}
          disabled={isDisabled}
        />
        <div className={isDisabled ? 'opacity-50 cursor-not-allowed user-select-none' : ''}>
          {t('qualifications.state.label')}
        </div>
      </div>

      {(item.stateAccredited ?? item.isStateAccredited) && (
        <AutoComplete
          label={t('qualifications.state.dropdownLabel')}
          placeholder={t('qualifications.state.placeholder')}
          value={item.accreditationCountry}
          options={countries.map((country) => country.value)}
          error={errors.accreditationCountry}
          onChange={(v) => {
            onChange(`${identifier}accreditationCountry`, v || '');
            setErrors((prev) => ({ ...prev, accreditationCountry: false }));
          }}
          getOptionLabel={(option) => option}
          disabled={isDisabled}
        />
      )}

      {errors.accreditationCountry && (
        <div className="text-red-600 text-xs mb-6">Please select accreditation country</div>
      )}

      <div className="grid grid-cols-2 col-gap-4 mb-6">
        <div className="flex flex-col gap-2">
          <DatePicker
            name={`${t('qualifications.start_date.label')}*`}
            placeholder={`${t('qualifications.start_date.placeholder')}*`}
            value={item.issueDate}
            onChange={(v) => handleIssueDateChange(v)}
            clickResetDate={() => !isDisabled && onChange(`${identifier}issueDate`, null)}
            validationMessage={errors.issueDate || errors.start ? 'Please select issue date' : null}
            views={['year', 'month']}
            format={shortMonthYearFormat}
            disableFuture={false}
            disabled={isDisabled}
          />
        </div>

        <DatePicker
          name={t('qualifications.end_date.label')}
          placeholder={`${t('qualifications.end_date.placeholder')}*`}
          value={item.expirationDate || null}
          onChange={(v) => onChange(`${identifier}expirationDate`, v)}
          clickResetDate={() => !isDisabled && onChange(`${identifier}expirationDate`, null)}
          // validationMessage={errors?.end}
          views={['year', 'month']}
          format={shortMonthYearFormat}
          disableFuture={false}
          minDate={item.issueDate || null}
          disabled={isDisabled}
        />
      </div>
      <Space size="small" direction="vertical" className="w-full mb-6">
        <label className="uppercase tracking-wide text-gray-600 text-xs font-semibold mb-2">
          <div className="flex flex-row gap-2 items-center">{t('qualifications.tags.label')}</div>
        </label>
        <CustomSelect
          className="w-full"
          mode="multiple"
          allowClear
          placeholder={t('qualifications.tags.placeholder')}
          options={tagsList.map((tagItem) => ({ value: tagItem.tag }))}
          onChange={(v) => handleSkillsChange(v)}
          value={
            item.skills?.map((s) => s.tag?.tag ?? s.tag) ??
            item.tags?.map((s) => s.value ?? s.tag) ??
            []
          }
          disabled={isDisabled}
        />
      </Space>
    </div>
  );
};

export default QualificationForm;

const CustomSelect = styled(Select)({
  '& .ant-select-open': {
    border: '1px solid #000000 !important',
    display: 'none !important',
  },
  '& .ant-select-selector': {
    minHeight: '42px !important',
    backgroundColor: '#EDF2F7 !important',
    border: '1px solid transparent !important',

    display: 'flex',
    alignItems: 'center',
  },
});
